import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supplements } from '../../../mocks/prestations';
import Summary from './Summary';

interface ClientInfoStepProps {
  data: any;
  updateData: (data: any) => void;
  prevStep: () => void;
}

export default function ClientInfoStep({
  data,
  updateData,
  prevStep,
}: ClientInfoStepProps) {
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    // ----- Validation -------------------------------------------------------
    if (
      !data.firstName ||
      !data.lastName ||
      !data.email ||
      !data.phone ||
      !data.vehicleType
    ) {
      setError('Veuillez remplir tous les champs obligatoires');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (
      data.location === 'domicile' &&
      (!data.address ||
        !data.postalCode ||
        !data.city ||
        data.distance === 0)
    ) {
      setError('Veuillez vérifier votre adresse et le calcul de distance');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setProcessing(true);
    setError('');

    try {
      // ----- Build reservation payload --------------------------------------
      const totalAmount = calculateTotal();

      const reservationData = {
        prestation_id: data.prestation.id,
        prestation_name: data.prestation.name,
        prestation_category: data.prestation.category,
        vehicle_size: data.size,
        base_price: Number(data.price) || 0,
        selected_options: data.options
          .map((id: string) => {
            const opt = supplements.find((s) => s.id === id);
            return opt ? { id: opt.id, name: opt.name, price: opt.price } : null;
          })
          .filter(Boolean),
        options_total: getOptionsTotal(),
        service_location: data.location,
        client_address:
          data.location === 'domicile'
            ? `${data.address}, ${data.postalCode} ${data.city}`
            : 'Z.I. Champ cheval 2, 1530 Payerne, Suisse',
        distance_km: data.location === 'domicile' ? data.distance : 0,
        travel_cost: data.location === 'domicile' ? data.travelCost : 0,
        reservation_date: data.date,
        reservation_time: data.time,
        client_first_name: data.firstName,
        client_last_name: data.lastName,
        client_email: data.email,
        client_phone: data.phone,
        client_postal_code: data.location === 'domicile' ? data.postalCode : null,
        client_city: data.location === 'domicile' ? data.city : null,
        vehicle_type: data.vehicleType,
        additional_info: data.additionalInfo,
        total_amount: totalAmount,
        payment_method: 'onsite',
        status: 'confirmed',
      };

      // ----- Call Supabase edge function ------------------------------------
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-manual-reservation`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ reservationData }),
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.error || result.message || 'Erreur lors de la création de la réservation',
        );
      }

      if (result.reservationId) {
        // Redirect to confirmation page using React Router
        navigate(`/confirmation?reservation_id=${result.reservationId}`);
      } else {
        throw new Error('ID de réservation manquant');
      }
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue. Veuillez réessayer.');
      setProcessing(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // ----- Helper functions ----------------------------------------------------
  const getOptionsTotal = () => {
    return data.options.reduce((total: number, optionId: string) => {
      const option = supplements.find((s) => s.id === optionId);
      return total + (option?.price || 0);
    }, 0);
  };

  const calculateTotal = () => {
    const basePrice = Number(data.price) || 0;
    const optionsTotal = getOptionsTotal();
    const travel = data.location === 'domicile' ? data.travelCost : 0;
    return Math.round((basePrice + optionsTotal + travel) * 100) / 100;
  };

  const toggleOption = (optionId: string) => {
    const newOptions = data.options.includes(optionId)
      ? data.options.filter((id: string) => id !== optionId)
      : [...data.options, optionId];
    updateData({ options: newOptions });
  };

  // ----- Render --------------------------------------------------------------
  return (
    <div className="bg-dark-card border border-dark-border rounded-2xl p-8">
      <button
        onClick={prevStep}
        className="flex items-center text-primary hover:text-primary/80 transition-colors duration-300 mb-6"
      >
        <i className="ri-arrow-left-line mr-2"></i>
        Retour
      </button>

      <h2 className="text-2xl font-bold text-white mb-8">Vos coordonnées</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          {/* ---- Personal details ---- */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-white font-semibold mb-2 text-sm">
                Prénom *
              </label>
              <input
                type="text"
                value={data.firstName}
                onChange={(e) => updateData({ firstName: e.target.value })}
                className="w-full p-4 bg-dark-bg border border-dark-border text-white rounded-lg focus:outline-none focus:border-primary"
                placeholder="Votre prénom"
              />
            </div>
            <div>
              <label className="block text-white font-semibold mb-2 text-sm">
                Nom *
              </label>
              <input
                type="text"
                value={data.lastName}
                onChange={(e) => updateData({ lastName: e.target.value })}
                className="w-full p-4 bg-dark-bg border border-dark-border text-white rounded-lg focus:outline-none focus:border-primary"
                placeholder="Votre nom"
              />
            </div>
          </div>

          <div>
            <label className="block text-white font-semibold mb-2 text-sm">
              Email *
            </label>
            <input
              type="email"
              value={data.email}
              onChange={(e) => updateData({ email: e.target.value })}
              className="w-full p-4 bg-dark-bg border border-dark-border text-white rounded-lg focus:outline-none focus:border-primary"
              placeholder="votre@email.com"
            />
          </div>

          <div>
            <label className="block text-white font-semibold mb-2 text-sm">
              Téléphone *
            </label>
            <input
              type="tel"
              value={data.phone}
              onChange={(e) => updateData({ phone: e.target.value })}
              className="w-full p-4 bg-dark-bg border border-dark-border text-white rounded-lg focus:outline-none focus:border-primary"
              placeholder="+41 79 123 45 67"
            />
          </div>

          <div>
            <label className="block text-white font-semibold mb-2 text-sm">
              Type de véhicule *
            </label>
            <input
              type="text"
              value={data.vehicleType}
              onChange={(e) => updateData({ vehicleType: e.target.value })}
              className="w-full p-4 bg-dark-bg border border-dark-border text-white rounded-lg focus:outline-none focus:border-primary"
              placeholder="Ex: BMW Série 3, Audi A4..."
            />
          </div>

          {/* ---- Additional info ---- */}
          <div>
            <label className="block text-white font-semibold mb-2 text-sm">
              Informations complémentaires
            </label>
            <textarea
              rows={3}
              maxLength={500}
              value={data.additionalInfo}
              onChange={(e) => updateData({ additionalInfo: e.target.value })}
              className="w-full p-4 bg-dark-bg border border-dark-border text-white rounded-lg focus:outline-none focus:border-primary resize-none"
              placeholder="Précisions sur votre véhicule, demandes particulières..."
            ></textarea>
            <p className="text-xs text-gray-500 mt-1">
              {data.additionalInfo.length}/500 caractères
            </p>
          </div>

          {/* ---- Supplements (optional) ---- */}
          {supplements.length > 0 && (
            <div className="pt-4 border-t border-dark-border">
              <label className="block text-white font-semibold mb-3 text-sm">
                Options supplémentaires
              </label>
              <div className="space-y-2">
                {supplements.map((supplement) => (
                  <label
                    key={supplement.id}
                    className="flex items-center justify-between p-3 bg-dark-bg border border-dark-border rounded-lg hover:border-primary/50 transition-all cursor-pointer"
                  >
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={data.options.includes(supplement.id)}
                        onChange={() => toggleOption(supplement.id)}
                        className="w-5 h-5 text-primary bg-dark-card border-dark-border rounded focus:ring-primary focus:ring-2 cursor-pointer"
                      />
                      <span className="text-white text-sm">{supplement.name}</span>
                    </div>
                    <span className="text-primary font-bold text-sm">
                      +{supplement.price} CHF
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ---- Summary component ---- */}
        <Summary data={data} getOptionsTotal={getOptionsTotal} calculateTotal={calculateTotal} />
      </div>

      {/* ---- Error message ---- */}
      {error && (
        <div className="mt-6 bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <i className="ri-error-warning-line text-red-400 text-xl"></i>
            <p className="text-red-400">{error}</p>
          </div>
        </div>
      )}

      {/* ---- Submit button ---- */}
      <button
        onClick={handleSubmit}
        disabled={processing}
        className="w-full mt-8 px-6 py-4 bg-gradient-to-r from-primary to-accent-sky text-dark-bg rounded-xl font-bold text-lg hover:shadow-2xl transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
      >
        {processing ? (
          <>
            <i className="ri-loader-4-line animate-spin text-xl"></i>
            <span>Confirmation en cours...</span>
          </>
        ) : (
          <>
            <i className="ri-check-double-line text-xl"></i>
            <span>Confirmer la réservation</span>
          </>
        )}
      </button>

      <p className="text-xs text-center text-gray-500 mt-4">
        <i className="ri-shield-check-line mr-1"></i>
        Vous recevrez un email de confirmation avec tous les détails
      </p>
    </div>
  );
}
